import CashiersRequest from '@/@api/cashiers'
import { ref, computed } from '@vue/composition-api'
// import store from '@/store'
// import { title } from '@core/utils/filter'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { debouncedWatch } from '@vueuse/core'

const cashiers = ref([])

export default function useCashiers() {
  // Use toast
  const toast = useToast()

  const refCashierListTable = ref(null)

  // const cashiers = ref([])

  // Table Handlers
  const tableColumns = [
    { key: 'id', label: '#', sortable: true },
    // { label: 'Imagen', key: 'image' },
    {
      label: 'Cajero',
      key: 'name',
      sortable: true,
      thStyle: { width: '100%' },
    },
    { label: 'Selector', key: 'selector', sortable: true },
    { label: 'Paises', key: 'countries', sortable: true },
    { label: 'Monedas', key: 'currencies', sortable: true },
    { label: 'Tipo', key: 'type', sortable: true },
    { label: 'Activo', key: 'status', sortable: true },
    { label: 'Acciones', key: 'actions' },
  ]
  const perPage = ref(10)
  const totalCashiers = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const loadingFilter = ref(false)
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)
  const roleFilter = ref(null)
  const planFilter = ref(null)
  const statusFilter = ref(null)
  const loadingResume = ref(true)

  const dataMeta = computed(() => {
    const localItemsCount = refCashierListTable.value ? refCashierListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalCashiers.value,
    }
  })

  const refetchData = () => {
    refCashierListTable.value.refresh()
  }

  debouncedWatch([currentPage, perPage, searchQuery, roleFilter, planFilter, statusFilter], () => {
    refetchData()
  }, { debounce: 400 })

  const fetchCashiers = (ctx, callback) => {
    loadingFilter.value = true
    CashiersRequest.list({
      params: {
        q: searchQuery.value,
        paginate: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        role: roleFilter.value,
        plan: planFilter.value,
        status: statusFilter.value,
      },
    })
      .then(response => {
        const { data, total } = response.data.data
        callback(data.map(cashier => ({ ...cashier, loading: false })))
        totalCashiers.value = total
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching cashiers list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .finally(() => {
        loadingFilter.value = false
      })
  }

  async function fetchCashiersResume() {
    try {
      loadingResume.value = true
      const { data } = await CashiersRequest.resume()
      return data.data
    } catch (error) {
      throw new Error(error)
    } finally {
      loadingResume.value = false
    }
  }

  async function fetchCashiersSelector(isMemo = false) {
    if (isMemo && cashiers.value.length) {
      return Promise.resolve(cashiers.value)
    }

    try {
      const { data } = await CashiersRequest.select()

      const list = data.data.map(cashier => ({
        id: cashier.id,
        value: cashier.iso?.toUpperCase() || cashier.id,
        label: cashier.name,
      }))

      cashiers.value = list

      return list
    } catch (error) {
      throw new Error(error)
    }
  }

  async function cashierStore(body) {
    try {
      const { data } = await CashiersRequest.store(body)

      toast({
        component: ToastificationContent,
        props: {
          title: data.message,
          icon: 'CheckIcon',
          variant: 'success',
        },
      })
    } catch (error) {
      throw new Error(error)
    }
  }

  async function cashierUpdate(id, body) {
    try {
      const { data } = await CashiersRequest.update(id, body)

      toast({
        component: ToastificationContent,
        props: {
          title: data.message,
          icon: 'CheckIcon',
          variant: 'success',
        },
      })
    } catch (error) {
      throw new Error(error)
    }
  }

  async function cashierDelete(id) {
    try {
      const { data } = await CashiersRequest.destroy(id)

      toast({
        component: ToastificationContent,
        props: {
          title: data.message,
          icon: 'CheckIcon',
          variant: 'success',
        },
      })
    } catch (error) {
      throw new Error(error)
    }
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  fetchCashiersSelector(true).then(list => {
    cashiers.value = list
  })

  const resolveIsoCashier = iso => {
    if (!iso) return ''

    const dic = cashiers.value.reduce((acc, cashier) => {
      acc[cashier.value] = cashier.label

      return acc
    }, {})

    return dic[iso] ?? iso
  }

  return {
    cashiers,
    fetchCashiers,
    fetchCashiersResume,
    fetchCashiersSelector,
    tableColumns,
    perPage,
    currentPage,
    totalCashiers,
    dataMeta,
    perPageOptions,
    searchQuery,
    loadingFilter,
    loadingResume,
    sortBy,
    isSortDirDesc,
    refCashierListTable,

    resolveIsoCashier,

    refetchData,
    cashierStore,
    cashierUpdate,
    cashierDelete,

    // Extra Filters
    roleFilter,
    planFilter,
    statusFilter,
  }
}
